import type { Level } from "building-types/levels/levelTypes";
import type {
  PostDefault,
  PostPlacementInstance,
  RailingDefaultSetting,
  RailingLinework,
} from "building-types/settings/railingTypes";
import { RailingInfillStyle } from "building-types/settings/railingTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { DIST_EPSILON } from "geom/core";
import { joinPolyLine2Sequence } from "geom/join";
import type { OBB2 } from "geom/obb2";
import { obb2ByThickenedEdge, obb2New } from "geom/obb2";
import { polyLine2Thicken } from "geom/offset";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2End,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2ReverseCopy,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeCount, polyLine3EdgeEnd, polyLine3EdgeStart } from "geom/polyLine3";
import { rect2ByCenterWidthHeight } from "geom/rect2";
import { vec2Dist } from "geom/vec2";
import { rotatePostRect2ToAlignWithVec2 } from "./posts";
import type { Path3OnSurfaces } from "./surfaces";

interface MakeRailingLinework2Options {
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly levelId: string;
  readonly levels: Table<Level>;
  readonly topRailHeight: number;
  readonly railingCutHeight: number;
  readonly lineworkWidth: number;
  readonly postPlacementInstances: PostPlacementInstance[];
  readonly post: PostDefault;
}

export function makeRailingLinework2({
  railingPathEdge3s,
  levelId,
  levels,
  topRailHeight,
  railingCutHeight,
  lineworkWidth,
  postPlacementInstances,
  post,
}: MakeRailingLinework2Options) {
  const levelLinework2s: LevelLinework[] = [];

  for (const pathPolyLine3 of railingPathEdge3s) {
    if (!pathPolyLine3.polyLine3) {
      continue;
    }

    const level = levels[levelId];
    const levelAboveFloorElevation =
      (level.levelAbove !== undefined ? levels[level.levelAbove]?.floorElevation : undefined) ||
      levels[levelId].topOfWall;
    const { polyLine3 } = pathPolyLine3;
    const heightToNextLevel =
      level.levelAbove === undefined ? Number.MAX_VALUE : levelAboveFloorElevation - levels[levelId].floorElevation;
    const nextLevelExists = level.levelAbove !== undefined;

    const edgeCount = polyLine3EdgeCount(polyLine3);

    for (let i = 0; i < edgeCount; i++) {
      const [sx, sy, szRaw] = polyLine3EdgeStart(polyLine3, i);
      const [ex, ey, ezRaw] = polyLine3EdgeEnd(polyLine3, i);

      const sz = szRaw + topRailHeight - levels[levelId].floorElevation;
      const ez = ezRaw + topRailHeight - levels[levelId].floorElevation;

      const edgeLength = vec2Dist(sx, sy, ex, ey);
      if (edgeLength < DIST_EPSILON) {
        continue;
      }

      let edge = polyLine2ByTwoPoints(sx, sy, ex, ey);

      if (Math.abs(sz - ez) < DIST_EPSILON) {
        // non-sloping
        if (sz <= railingCutHeight) {
          levelLinework2s.push({ levelId: levelId, polyLine2: edge, aboveViewCut: false });
        } else if (sz > railingCutHeight && sz <= heightToNextLevel) {
          levelLinework2s.push({ levelId: levelId, polyLine2: edge, aboveViewCut: true });
        } else {
          nextLevelExists
            ? levelLinework2s.push({ levelId: level.levelAbove, polyLine2: edge, aboveViewCut: false })
            : levelLinework2s.push({ levelId: levelId, polyLine2: edge, aboveViewCut: true });
        }
      } else {
        const minZ = Math.min(sz, ez);
        const maxZ = Math.max(sz, ez);

        if (minZ <= railingCutHeight && maxZ <= railingCutHeight) {
          levelLinework2s.push({ levelId: levelId, polyLine2: edge, aboveViewCut: false });
        } else if (
          minZ > railingCutHeight &&
          maxZ > railingCutHeight &&
          minZ <= heightToNextLevel &&
          maxZ <= heightToNextLevel
        ) {
          levelLinework2s.push({ levelId: levelId, polyLine2: edge, aboveViewCut: true });
        } else {
          const slope = (maxZ - minZ) / edgeLength;

          let reverse = false;
          if (sz > ez) {
            reverse = true;
            edge = polyLine2ReverseCopy(edge);
          }

          // GOING UP STAIRS
          const [x0, y0] = polyLine2EdgeEval(edge, 0, 0);
          const [x3, y3] = polyLine2EdgeEval(edge, 0, edgeLength);
          const stairCutIntersectionDist = (railingCutHeight - minZ) / slope;
          const edgePassesThroughStairCutZ = stairCutIntersectionDist >= 0 && stairCutIntersectionDist <= edgeLength;
          const [x1, y1] = polyLine2EdgeEval(edge, 0, stairCutIntersectionDist);
          if (edgePassesThroughStairCutZ) {
            levelLinework2s.push({
              levelId: levelId,
              polyLine2: reverse ? polyLine2ByTwoPoints(x1, y1, x0, y0) : polyLine2ByTwoPoints(x0, y0, x1, y1),
              aboveViewCut: false,
            });
          }

          const nextLevelIntersectionDist = (heightToNextLevel - minZ) / slope;
          const edgePassesThroughNextLevelZ = nextLevelIntersectionDist >= 0 && nextLevelIntersectionDist <= edgeLength;
          const [x2, y2] = polyLine2EdgeEval(edge, 0, nextLevelIntersectionDist);

          if (edgePassesThroughStairCutZ && !edgePassesThroughNextLevelZ) {
            levelLinework2s.push({
              levelId: levelId,
              polyLine2: reverse ? polyLine2ByTwoPoints(x3, y3, x1, y1) : polyLine2ByTwoPoints(x1, y1, x3, y3),
              aboveViewCut: true,
            });
          } else if (edgePassesThroughStairCutZ && edgePassesThroughNextLevelZ) {
            levelLinework2s.push({
              levelId: levelId,
              polyLine2: reverse ? polyLine2ByTwoPoints(x2, y2, x1, y1) : polyLine2ByTwoPoints(x1, y1, x2, y2),
              aboveViewCut: true,
            });
            levelLinework2s.push({
              levelId: levelId,
              polyLine2: reverse ? polyLine2ByTwoPoints(x3, y3, x2, y2) : polyLine2ByTwoPoints(x2, y2, x3, y3),
              aboveViewCut: true,
            });
          } else if (edgePassesThroughNextLevelZ) {
            levelLinework2s.push({
              levelId: levelId,
              polyLine2: reverse ? polyLine2ByTwoPoints(x2, y2, x0, y0) : polyLine2ByTwoPoints(x0, y0, x2, y2),
              aboveViewCut: true,
            });
            levelLinework2s.push({
              levelId: levelId,
              polyLine2: reverse ? polyLine2ByTwoPoints(x3, y3, x2, y2) : polyLine2ByTwoPoints(x2, y2, x3, y3),
              aboveViewCut: true,
            });
          }
        }
      }
    }
  }

  const railingLinework2: { [levelId: string]: RailingLinework } = {};
  for (const levelLinework of levelLinework2s.sort(
    (a, b) => levels[a.levelId].floorIndex - levels[b.levelId].floorIndex,
  )) {
    const { levelId: lineworkLevelId } = levelLinework;
    const linework: RailingLinework = railingLinework2[lineworkLevelId] ?? {
      polyLine2s: [],
      polyLine2sAboveViewCut: [],
      levelId: lineworkLevelId,
      postPolyLine2s: [],
      obbs: [],
      obbsAboveViewCut: [],
    };

    const { width, depth } = post;
    const postRect = rect2ByCenterWidthHeight(0, 0, depth, width);
    for (const { x, y, tx, ty, id } of postPlacementInstances) {
      const post2 = rotatePostRect2ToAlignWithVec2(tx, ty, x, y, postRect);
      const obb = obb2New(x, y, depth / 2, width / 2, Math.atan2(ty, tx));
      linework.postPolyLine2s.push({
        id,
        polyLine2: post2,
        obb,
      });
    }

    if (levelLinework.aboveViewCut) {
      linework.polyLine2sAboveViewCut.push(levelLinework.polyLine2);
    } else {
      linework.polyLine2s.push(levelLinework.polyLine2);
    }

    railingLinework2[lineworkLevelId] = linework;
  }

  for (const key in railingLinework2) {
    const levelLinework = railingLinework2[key];
    const { polyLine2s, polyLine2sAboveViewCut } = levelLinework;

    const { polyLine2s: joinedPolyLine2s, obbs: joinedObbs } = joinSequentialPolyLine2s(polyLine2s, lineworkWidth);
    const { polyLine2s: joinedPolyLine2sAboveViewCut, obbs: joinedObbsAboveViewCut } = joinSequentialPolyLine2s(
      polyLine2sAboveViewCut,
      lineworkWidth,
    );

    levelLinework.polyLine2s = joinedPolyLine2s;
    levelLinework.obbs = joinedObbs;
    levelLinework.polyLine2sAboveViewCut = joinedPolyLine2sAboveViewCut;
    levelLinework.obbsAboveViewCut = joinedObbsAboveViewCut;
  }
  return railingLinework2;
}

function collectObbsForPolyLine2(polyLine2: PolyLine2, lineworkWidth: number, obbs: OBB2[]) {
  const edgeCount = polyLine2EdgeCount(polyLine2);
  for (let i = 0; i < edgeCount; i++) {
    const isFirstEdge = i === 0;
    const isLastEdge = i === edgeCount - 1;

    const edge = polyLine2Copy(polyLine2Edge(polyLine2, i));

    if (!isFirstEdge) {
      polyLine2ExtendStart(edge, lineworkWidth / 2);
    }

    if (!isLastEdge) {
      polyLine2ExtendEnd(edge, lineworkWidth / 2);
    }

    const obb = obb2ByThickenedEdge(edge, 0, lineworkWidth, true);
    obbs.push(obb);
  }
}

function joinSequentialPolyLine2s(
  polyLine2s: PolyLine2[],
  lineworkWidth: number,
): {
  readonly polyLine2s: PolyLine2[];
  readonly obbs: OBB2[];
} {
  const newPolys: PolyLine2[] = [];
  const obbs: OBB2[] = [];
  const polys = polyLine2s;
  const polysLength = polys.length;

  let currentPolyChain: PolyLine2 | undefined;
  for (let i = 0; i < polysLength; i++) {
    const poly = polys[i];
    if (currentPolyChain) {
      const [ex, ey] = polyLine2End(currentPolyChain);
      const [sx, sy] = polyLine2Start(poly);
      if (vec2Dist(sx, sy, ex, ey) < DIST_EPSILON) {
        currentPolyChain = joinPolyLine2Sequence([currentPolyChain, poly]);
      } else {
        newPolys.push(polyLine2Thicken(currentPolyChain, lineworkWidth, true));
        collectObbsForPolyLine2(currentPolyChain, lineworkWidth, obbs);
        currentPolyChain = poly;
      }
    } else {
      currentPolyChain = poly;
    }
  }

  if (currentPolyChain) {
    newPolys.push(polyLine2Thicken(currentPolyChain, lineworkWidth, true));
    collectObbsForPolyLine2(currentPolyChain, lineworkWidth, obbs);
  }

  return { polyLine2s: newPolys, obbs };
}

export function getRailingLineworkWidth(settings: RailingDefaultSetting) {
  const { hasTopRail, hasBottomRail, hasPosts, infillStyle, post, topRail, bottomRail, infillDepth } = settings;

  let lineworkWidth = 0;
  const hasInfill = infillStyle === RailingInfillStyle.BALUSTERS || RailingInfillStyle.SOLID;

  if (hasTopRail && hasBottomRail) {
    assertIsNotUndefined(topRail);
    assertIsNotUndefined(bottomRail);
    lineworkWidth = Math.max(topRail.width, bottomRail.width);
  } else if (hasTopRail) {
    assertIsNotUndefined(topRail);
    lineworkWidth = topRail.width;
  } else if (hasBottomRail) {
    assertIsNotUndefined(bottomRail);
    lineworkWidth = bottomRail.width;
  } else if (hasPosts && hasInfill) {
    assertIsNotUndefined(post);
    assertIsNotUndefined(infillDepth);
    lineworkWidth = Math.max(post.depth, infillDepth);
  } else if (hasPosts) {
    assertIsNotUndefined(post);
    lineworkWidth = post.depth;
  } else if (hasInfill) {
    assertIsNotUndefined(infillDepth);
    lineworkWidth = infillDepth;
  }
  return lineworkWidth;
}

interface LevelLinework {
  readonly levelId: string;
  readonly polyLine2: PolyLine2;
  readonly aboveViewCut: boolean;
}
